import _, { sortBy } from 'lodash';
import React, { useState } from 'react';
import { OteGameOtePlayerOteTeam } from '~/src/models/OteGameOtePlayerOteTeam';
import { OteGameOteTeam } from '~/src/models/OteGameOteTeam';
import { OtePlayer } from '~/src/models/OtePlayer';
import { OteTeam } from '~/src/models/OteTeam';
import { colorFromInt } from '~/src/overtime-lib/src/www/lib';
import { imgOteLogoWhiteSvg } from '../../images';
import { OteGame } from '../../models/OteGame';
import { getThemeTeam, OteTheme } from '../../models/OteTheme';
import './TeamLeaders.scss';

const getInitial = (first_name: string, last_name: string) => {
	if (last_name === 'Thompson' && (first_name === 'Amen' || first_name === 'Ausar')) {
		return `${first_name.substring(0, 2)}. ${last_name}`;
	}

	return `${first_name[0]}. ${last_name}`;
};

const TeamLogo = ({ ote_team, ote_game }: { ote_team: OteTeam; ote_game: OteGame }) => {
	const [themeTeam, setThemeTeam] = useState<OteTheme>(getThemeTeam(ote_team?.id, ote_game.theme_name));
	const [logoPath, setLogoPath] = useState<string>(
		themeTeam
			? `https://images.overtime.tv/${themeTeam.logo_path}`
			: ote_team?.small_logo_path
				? `https://images.overtime.tv/${ote_team?.small_logo_path}?width=80&format=webp`
				: ote_team?.logo_path
					? `https://images.overtime.tv/${ote_team?.logo_path}?width=80&format=webp`
					: `${imgOteLogoWhiteSvg}`,
	);
	const [primaryColor, setPrimaryColor] = useState<number>(
		themeTeam ? themeTeam.primary_color : ote_team?.primary_color,
	);

	return (
		<div
			className="TeamLogo"
			style={{
				backgroundColor: colorFromInt(primaryColor),
				backgroundImage: `url(${logoPath})`,
			}}
		>
			{!ote_team?.small_logo_path && !ote_team?.logo_path ? ote_team?.team_code?.[0] : null}
		</div>
	);
};

const PlayerProfile = ({
	game,
	player,
	team,
	stat,
}: {
	game: OteGame;
	player: OtePlayer;
	team: OteTeam;
	stat: string;
}) => {
	const player_profile_url = player?.is_current && player?.is_ote && team.is_current ? `/players/${player.id}` : null;

	const [themeTeam, setThemeTeam] = useState<OteTheme>(getThemeTeam(team?.id, game.theme_name));
	const [primaryColor, setPrimaryColor] = useState<number>(themeTeam ? themeTeam.primary_color : team?.primary_color);

	return (
		<div className="Profile">
			{player?.image_path ? (
				<a href={player_profile_url}>
					<img
						alt={player.full_name}
						style={{ backgroundColor: colorFromInt(primaryColor) }}
						src={`https://images.overtime.tv/${player.image_path}?format=webp&width=160`}
					/>
				</a>
			) : (
				<TeamLogo ote_game={game} ote_team={team} />
			)}
			<div className="Name">
				{player ? (
					<>
						<a className="mobile-only" href={player_profile_url}>
							{getInitial(player.first_name, player.family_name)}
						</a>
						<a className="desktop-only" href={player_profile_url}>
							{player.first_name} {player.family_name}
						</a>
					</>
				) : null}
			</div>
			<div className="Position">
				{player?.position ? `${player.position} / ` : ''}
				{team.name}
			</div>
			<div className="Stat">{stat}</div>
		</div>
	);
};

const _TeamLeaders = ({
	game,
	stats,
	gameTeam,
	players,
}: {
	game: OteGame;
	stats: string[][];
	gameTeam: OteGameOteTeam;
	players: OteGameOtePlayerOteTeam[];
}) => {
	const teamPlayers = players
		.filter(Boolean)
		.filter((p) => p?.ote_team_id === gameTeam?.ote_team_id && p.ote_game_id === gameTeam?.ote_game_id);

	const topPlayers = stats.map((s) => {
		// sort all players from lowest to highest in the stat then pop the last one (highest stat) off to be stored in "statLeader"
		const statLeader = sortBy(teamPlayers, [
			(p) => (_.isNil(p[s[0]]) ? 0 : p[s[0]]),
			(p) => (_.isNil(p.plus_minus) ? -Infinity : p.plus_minus),
		]).pop();
		if (!statLeader) {
			return { stat: s[1], value: null, player: null };
		}
		return { stat: s[1], value: statLeader[s[0]], player: statLeader };
	});

	return (
		<div className="Team">
			{topPlayers.map((p) => (
				<div className="Player" key={p.stat}>
					<PlayerProfile
						game={game}
						player={p.value > 0 ? p.player.ote_player : null}
						team={gameTeam.ote_team}
						stat={`${p.value > 0 ? p.value : '–'} ${p.stat}`}
					/>
				</div>
			))}
		</div>
	);
};

const TeamLeaders = ({
	game,
	players,
	awayGameTeam,
	homeGameTeam,
}: {
	game: OteGame;
	players: OteGameOtePlayerOteTeam[];
	awayGameTeam: OteGameOteTeam;
	homeGameTeam: OteGameOteTeam;
}) => {
	const stats = [
		['points', 'pts'],
		['rebounds_total', 'reb'],
		['assists', 'ast'],
	];

	return awayGameTeam?.ote_team_id && homeGameTeam?.ote_team_id && players?.length > 0 ? (
		<div className="GameTeamLeaders">
			<h2>Team Leaders</h2>
			<div className="LeadersContainer">
				<_TeamLeaders game={game} stats={stats} players={players} gameTeam={awayGameTeam}></_TeamLeaders>
				<div className="StatNames">
					{stats.map((s) => (
						<div key={s[0]}>{s[1]}</div>
					))}
				</div>
				<_TeamLeaders game={game} stats={stats} players={players} gameTeam={homeGameTeam}></_TeamLeaders>
			</div>
		</div>
	) : null;
};
export default TeamLeaders;
